import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExhibitorPairStatus, IExhibitorPair } from "@evennt-tech/api-v2-core";
import { uniq } from "lodash";
import useInvalidateOnShowNotifications from "hooks/useInvalidateOnShowNotifications";
import {
  EXHIBITOR_PAIR_REALTIME_KEYS,
  exhibitorPairsQueryKey,
  fetchExhibitorPairsByStandIds,
} from "./exhibitorPairQueries";

type UseExhibitorPairsByStandIdsArgs = {
  showId?: string;
  standIds?: string[];
  legacyStandIds?: string[];
  status?: ExhibitorPairStatus;
};

type UseExhibitorPairsByStandIdsResult = [IExhibitorPair[], boolean];

const useExhibitorPairsByStandIds = ({
  showId,
  standIds = [],
  legacyStandIds = [],
  status,
}: UseExhibitorPairsByStandIdsArgs): UseExhibitorPairsByStandIdsResult => {
  const sortedStandIds = useMemo(() => uniq(standIds).sort(), [standIds]);
  const sortedLegacyStandIds = useMemo(() => uniq(legacyStandIds).sort(), [
    legacyStandIds,
  ]);
  const hasIds = sortedStandIds.length > 0 || sortedLegacyStandIds.length > 0;

  const { data, isFetching } = useQuery({
    queryKey: [
      ...exhibitorPairsQueryKey(showId),
      "by-stands",
      status ?? null,
      sortedStandIds,
      sortedLegacyStandIds,
    ],
    queryFn: () =>
      fetchExhibitorPairsByStandIds({
        showId: showId as string,
        standIds: sortedStandIds,
        legacyStandIds: sortedLegacyStandIds,
        status,
      }),
    enabled: !!showId && hasIds,
    refetchOnWindowFocus: false,
  });

  useInvalidateOnShowNotifications({
    showId,
    keys: EXHIBITOR_PAIR_REALTIME_KEYS,
    queryKey: exhibitorPairsQueryKey(showId),
  });

  const pairs = useMemo(() => (hasIds ? data ?? [] : []), [data, hasIds]);

  return [pairs, isFetching];
};

export default useExhibitorPairsByStandIds;
